<template>
  <header class="site-header">
    <div class="container header-container">
      <div class="logo">
        <router-link to="/">
          <img :src="getLogoUrl()" :alt="headerContent.title + ' Logo'" />
        </router-link>
      </div>
      <nav class="main-nav">
        <ul>
          <li><router-link to="/">{{ headerContent.nav_home }}</router-link></li>
          <li><router-link to="/about">{{ headerContent.nav_about }}</router-link></li>
          <li><router-link to="/courses">{{ headerContent.nav_courses }}</router-link></li>
          <li><router-link to="/notices">{{ headerContent.nav_notices }}</router-link></li>
          <li><router-link to="/access">{{ headerContent.nav_access }}</router-link></li>
          <li><router-link to="/contact">{{ headerContent.nav_contact }}</router-link></li>
          <li v-if="isAdmin">
              <router-link to="/admin" class="admin-link">管理</router-link>
          </li>
        </ul>
      </nav>

      <!-- Mobile Visible Links -->
      <nav class="mobile-visible-links">
          <ul>
              <li><router-link to="/">{{ headerContent.nav_home }}</router-link></li>
              <li><router-link to="/contact">{{ headerContent.nav_contact }}</router-link></li>
          </ul>
      </nav>

      <!-- Auth links and Mobile Toggle -->
      <div class="header-right-controls">
        <div class="auth-links">
          <div v-if="isLoading"><span>Loading...</span></div>
          <div v-else>
            <span v-if="currentUser">
              <!-- 將 email 包裹在 router-link 中 -->
              <router-link to="/profile" class="user-profile-link">
                  {{ currentUser.email }}
              </router-link>
              <button @click="handleLogout" class="btn btn-sm btn-outline logout-btn">ログアウト</button>
            </span>
            <span v-else>
              <router-link to="/auth" class="btn btn-sm btn-primary login-link">ログイン</router-link>
              <!-- 可以考慮保留註冊鏈接，或者只在 /auth 頁面處理 -->
              <!-- <router-link to="/auth?mode=register">登錄</router-link> -->
            </span>
          </div>
        </div>
        <button class="mobile-nav-toggle" @click="toggleMobileNav" :class="{ open: isMobileNavOpen }" aria-label="Toggle navigation">
            <span class="hamburger-icon"></span>
        </button>
      </div>
    </div>

    <!-- Mobile Dropdown Nav -->
    <nav class="mobile-nav" :class="{ open: isMobileNavOpen }">
        <ul>
          <li><router-link to="/about" @click="closeMobileNav">{{ headerContent.nav_about }}</router-link></li>
          <li><router-link to="/courses" @click="closeMobileNav">{{ headerContent.nav_courses }}</router-link></li>
          <li><router-link to="/notices" @click="closeMobileNav">{{ headerContent.nav_notices }}</router-link></li>
          <li><router-link to="/access" @click="closeMobileNav">{{ headerContent.nav_access }}</router-link></li>
          <li v-if="isAdmin">
              <router-link to="/admin" class="admin-link" @click="closeMobileNav">管理</router-link>
          </li>
           <!-- Mobile Auth Links -->
          <li v-if="!isLoading && currentUser">
            <router-link to="/profile" class="user-profile-link" @click="closeMobileNav">
                {{ currentUser.email }}
            </router-link>
          </li>
          <li v-if="!isLoading && currentUser">
              <button @click="() => { handleLogout(); closeMobileNav(); }" class="btn btn-sm btn-outline logout-btn-mobile">ログアウト</button>
          </li>
           <li v-if="!isLoading && !currentUser">
              <router-link to="/auth" class="btn btn-sm btn-primary login-link-mobile" @click="closeMobileNav">ログイン</router-link>
          </li>
        </ul>
      </nav>
  </header>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { auth, onAuthStateChanged, signOut, getUserRole } from '@/api';

const router = useRouter();
const currentUser = ref(null);
const isAdmin = ref(false);
const isLoading = ref(true);
const isMobileNavOpen = ref(false);

// Header content state
const headerContent = ref({
  title: '皆の英語教室',
  logo: '',
  nav_home: 'ホーム',
  nav_about: '教室紹介',
  nav_courses: 'コース・料金',
  nav_notices: 'お知らせ',
  nav_access: 'アクセス',
  nav_contact: 'お問い合わせ'
});

let unsubscribeAuthState;

// Fetch header content
const fetchHeaderContent = async () => {
  try {
    const response = await fetch('https://enen.saitsuki.uk/api/page-content/header');
    if (response.ok) {
      const data = await response.json();
      const content = data.data;
      
      headerContent.value = {
        title: content.header_title || '皆の英語教室',
        logo: content.header_logo || '',
        nav_home: content.header_nav_home || 'ホーム',
        nav_about: content.header_nav_about || '教室紹介',
        nav_courses: content.header_nav_courses || 'コース・料金',
        nav_notices: content.header_nav_notices || 'お知らせ',
        nav_access: content.header_nav_access || 'アクセス',
        nav_contact: content.header_nav_contact || 'お問い合わせ'
      };
    }
  } catch (error) {
    console.error('Header content fetch error:', error);
  }
};

// Get logo URL with fallback
const getLogoUrl = () => {
  if (headerContent.value.logo && headerContent.value.logo.startsWith('http')) {
    return headerContent.value.logo;
  }
  // Default logo
  return require('@/assets/images/logo_transparent.png');
};

// Fetch user and role on mount
onMounted(() => {
  fetchHeaderContent();
  
  unsubscribeAuthState = onAuthStateChanged(auth, async (user) => {
    isLoading.value = true;
    if (user) {
      currentUser.value = user;
      isAdmin.value = await getUserRole(user) === 'admin';
    } else {
      currentUser.value = null;
      isAdmin.value = false;
    }
    isLoading.value = false;
  });
});

// Unsubscribe when component is unmounted
onUnmounted(() => {
  if (unsubscribeAuthState) {
    unsubscribeAuthState();
  }
});

// Logout handler
const handleLogout = async () => {
  try {
    await signOut(auth);
    currentUser.value = null;
    isAdmin.value = false;
    router.push('/');
  } catch (error) {
    console.error('Logout failed:', error);
  }
};

// Mobile nav toggle
const toggleMobileNav = () => {
  isMobileNavOpen.value = !isMobileNavOpen.value;
};

const closeMobileNav = () => {
  isMobileNavOpen.value = false;
};
</script>

<style scoped>
/* Existing styles */
.site-header {
  background-color: var(--header-bg-color, #ffffff);
  padding: 15px 0;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 1000; /* Ensure header stays on top */
}

.header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo img {
  height: 50px; /* Adjust as needed */
  width: auto;
}

.main-nav ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  gap: 25px; /* Space between nav items */
}

.main-nav a {
  text-decoration: none;
  color: var(--text-color, #333);
  font-weight: 500;
  transition: color 0.3s ease;
}

.main-nav a:hover,
.main-nav a.router-link-active {
  color: var(--primary-color, #007bff);
}

.admin-link {
  color: #dc3545; /* Bootstrap danger color */
  font-weight: bold;
}
.admin-link:hover,
.admin-link.router-link-active {
   color: #a71d2a;
}

.auth-links {
  display: flex;
  align-items: center;
  gap: 10px; /* Space between login/logout */
}

/* Style the user profile link */
.user-profile-link {
    color: var(--primary-color); /* Make it stand out a bit */
    text-decoration: none;
    font-weight: 500;
    margin-right: 5px; /* Add some space before logout */
    transition: color 0.3s ease;
    /* Ensure it doesn't wrap unexpectedly */
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 150px; /* Adjust as needed */
    display: inline-block; /* Needed for max-width and text-overflow */
    vertical-align: middle; /* Align with button */
}
.user-profile-link:hover {
    color: var(--secondary-color);
    text-decoration: underline;
}

/* Adjust button styles if needed */
.auth-links .btn {
  padding: 6px 12px; /* Slightly smaller buttons */
  font-size: 14px;
  /* Ensure button doesn't shrink */
  flex-shrink: 0;
  vertical-align: middle; /* Align with link */
}

.logout-btn {
    /* 強制應用 outline button 的核心樣式 */
    background-color: transparent; /* 確保背景透明 */
    border: 1px solid var(--primary-color); /* 設置邊框顏色 */
    color: var(--primary-color); /* 設置文字顏色 */
}
.logout-btn:hover {
    background-color: var(--primary-color); /* Hover 時改變背景 */
    color: white; /* Hover 時改變文字顏色 */
}

.login-link {
     background-color: var(--primary-color);
    color: white;
    border: 1px solid var(--primary-color); /* Add border for consistency */
}
.login-link:hover {
     background-color: var(--secondary-color);
     border-color: var(--secondary-color);
}

/* Mobile Navigation Styles */
.mobile-nav-toggle {
  display: none; /* Hidden by default */
  background: none;
  border: none;
  cursor: pointer;
  padding: 10px;
  z-index: 1001; /* Ensure toggle is clickable */
}

.hamburger-icon {
  display: block;
  width: 25px;
  height: 3px;
  background-color: var(--text-color, #333);
  position: relative;
  transition: background-color 0.3s ease;
}

.hamburger-icon::before,
.hamburger-icon::after {
  content: '';
  position: absolute;
  left: 0;
  width: 100%;
  height: 3px;
  background-color: var(--text-color, #333);
  transition: transform 0.3s ease;
}

.hamburger-icon::before {
  top: -8px;
}

.hamburger-icon::after {
  bottom: -8px;
}

/* Styles for when mobile nav is open */
.mobile-nav-toggle.open .hamburger-icon {
  background-color: transparent; /* Hide middle line */
}

.mobile-nav-toggle.open .hamburger-icon::before {
  transform: translateY(8px) rotate(45deg);
}

.mobile-nav-toggle.open .hamburger-icon::after {
  transform: translateY(-8px) rotate(-45deg);
}

.mobile-nav {
  display: none; 
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  /* background-color: var(--header-bg-color, #ffffff); */ /* Remove solid background */
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-height: 0;
  overflow-y: auto; 
  transition: max-height 0.5s ease-in-out, padding 0.5s ease-in-out, background-color 0.5s ease, backdrop-filter 0.5s ease; /* Added transitions */
  padding: 0;
  z-index: 1000;
}

.mobile-nav.open {
  display: block; 
  max-height: calc(100vh - 70px); /* Adjust height based on header size */
  padding: 20px 0; 

  /* Stronger Frosted glass effect */
  background-color: rgba(255, 255, 255, 0.6); /* Decreased opacity */
  backdrop-filter: blur(12px); /* Increased blur */
  -webkit-backdrop-filter: blur(12px); /* Safari prefix */
}

.mobile-nav ul {
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: center;
}

.mobile-nav li {
  padding: 0 20px; /* Adjust padding */
  border-bottom: 1px solid #eee;
}
.mobile-nav li:last-child {
  border-bottom: none;
}

/* Style for standard mobile nav links */
.mobile-nav li a:not(.btn),
.mobile-nav li .user-profile-link { /* Apply similar style to profile link */
  display: block;
  text-decoration: none;
  color: var(--text-color, #333);
  font-weight: 500;
  padding: 15px 0; /* Increased padding for better tap target */
  transition: color 0.3s ease;
}

/* Style for profile link */
.mobile-nav li .user-profile-link {
    color: var(--primary-color); /* Match desktop link color */
    /* Optional: Add ellipsis for long emails */
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.mobile-nav li a:not(.btn):hover,
.mobile-nav li .user-profile-link:hover {
  color: var(--primary-color, #007bff);
}

.mobile-nav li a.router-link-active {
  color: var(--primary-color, #007bff); /* Highlight active link */
  font-weight: bold;
}

.mobile-nav .admin-link { color: #dc3545; }
.mobile-nav .admin-link:hover, .mobile-nav .admin-link.router-link-active { color: #a71d2a; }

/* Style for Auth buttons in mobile nav */
.mobile-nav li button,
.mobile-nav li a.btn { /* Target both button and links styled as buttons */
    width: calc(100% - 40px); /* Adjust width to fit padding */
    margin: 15px auto; /* Add margin top/bottom */
    padding: 12px 15px; /* Adjust padding */
    display: block;
    box-sizing: border-box; /* Include padding in width */
    font-size: 15px; /* Slightly larger font */
}

/* Specific styles for logout/login buttons */
.mobile-nav .logout-btn-mobile {
    /* Inherit base button styles + specific outline */
    background-color: transparent;
    border: 1px solid var(--primary-color);
    color: var(--primary-color);
}
.mobile-nav .logout-btn-mobile:hover {
    background-color: var(--primary-color);
    color: white;
}

.mobile-nav .login-link-mobile {
    /* Inherit base button styles + specific primary */
    background-color: var(--primary-color);
    color: white;
    border: 1px solid var(--primary-color);
}
.mobile-nav .login-link-mobile:hover {
     background-color: var(--secondary-color);
     border-color: var(--secondary-color);
}

/* New container for right-side controls */
.header-right-controls {
    display: flex;
    align-items: center;
    gap: 15px; /* Adjust gap between auth and toggle if needed */
}

/* Mobile Visible Links Styles */
.mobile-visible-links {
    display: none; /* Hidden by default */
}
.mobile-visible-links ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    gap: 15px; /* Space between Home and Contact */
}
.mobile-visible-links a {
    text-decoration: none;
    color: var(--text-color, #333);
    font-weight: 500;
    font-size: 14px; /* Slightly smaller for mobile header */
    transition: color 0.3s ease;
}
.mobile-visible-links a:hover,
.mobile-visible-links a.router-link-active {
  color: var(--primary-color, #007bff);
}

@media (max-width: 992px) {
  .main-nav, .auth-links { display: none; } /* Hide desktop nav and auth */
  .mobile-nav-toggle { display: block; } /* Show hamburger */
  .mobile-visible-links { display: flex; align-items: center; } /* Show mobile header links */

  /* Adjust header container spacing for mobile */
  .header-container {
      /* space-between might push mobile links too far left? */
      /* Let's try managing flex grow */
  }
  .logo {
      flex-shrink: 0; /* Don't shrink logo */
  }
  .mobile-visible-links {
       /* margin: 0 auto; /* Center the links? */ 
       margin-left: 20px; /* Add space after logo */
       margin-right: auto; /* Push toggle to the right */
  }
  .header-right-controls {
      flex-shrink: 0; /* Don't shrink toggle button area */
  }
}

@media (max-width: 768px) {
  .logo img {
    height: 55px; /* Increased logo height */
  }
  /* Further adjust spacing if needed */
  .mobile-visible-links {
       gap: 10px; /* Reduce gap */
       margin-left: 15px;
  }
  .mobile-visible-links a {
       font-size: 13px; /* Slightly smaller font */
  }

  .header-container {
      padding-left: 15px;
      padding-right: 15px;
  }
  .mobile-nav.open { max-height: calc(100vh - 70px); /* Adjust height based on new logo/header size */ }
}
</style> 